"use client"

import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import Link from "next/link"
import Image from "next/image"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry, isPathAllowedInCountry } from "@/lib/country-gates"
import { useLivePrices } from "@/lib/use-live-prices"
import {
  isBookingOpen,
  isComingSoon,
  isWaitlistOnly,
  type Country,
} from "@/i18n/config"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"
import { cn } from "@/lib/utils"
import type { NavbarItem } from "./types"
import { blobMediaUrl } from "@/lib/blob-url"
import { baseBloodTest } from "@/lib/base-blood-test"

/**
 * The imaging add-ons hardcoded in the imaging column below, as bare
 * `/tests/<slug>` paths. Kept next to the JSX it mirrors: the column headings
 * ("Bildgebung", "Add-ons") are only worth rendering while at least one of
 * these links survives this market's CMS service state / page gates.
 */
const IMAGING_ADD_ON_PATHS = [
  "/tests/calcium-score",
  "/tests/body-composition",
  "/tests/dexa-scan",
  "/tests/brain-volumetry",
  "/tests/mamma-mri",
  "/tests/prostate-mri",
  "/tests/pelvic-mri",
]

export function DropdownMenu({
  items,
}: {
  items: { label: string; href: string }[]
}) {
  return (
    <div className="absolute top-full left-1/2 z-50 mt-1 min-w-[180px] -translate-x-1/2 rounded-[12px] border border-neutral-100 bg-white py-1 shadow-md">
      {items.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          className="hover:text-aubergine-900 block px-4 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          {item.label}
        </Link>
      ))}
    </div>
  )
}

export function HowItWorksMegaMenu({
  megaItem,
  onPlayVideo,
}: {
  megaItem: NavbarItem
  onPlayVideo: (src: string) => void
}) {
  const t = useTranslations("nav")
  const localePath = useLocalePath()
  return (
    <div className="ml-auto flex max-w-[1100px] gap-8 px-8 py-6">
      {/* Left: nav items */}
      <div className="flex-1">
        <p className="type-label-md mb-4 px-4 pt-2 text-neutral-600">
          {t("getStarted")}
        </p>
        <div className="grid grid-cols-2">
          {megaItem.children?.map((child) => (
            <Link
              key={child.href + child.label}
              href={child.href}
              className="flex items-center gap-4 rounded-[16px] p-4 hover:bg-white"
            >
              {child.imageSrc && (
                <div className="h-[58px] w-[58px] flex-shrink-0">
                  <Image
                    src={child.imageSrc}
                    alt=""
                    width={58}
                    height={58}
                    className="h-[58px] w-[58px] rounded-[8px] object-cover"
                  />
                </div>
              )}
              <div>
                <p className="type-title-md text-foreground">{child.label}</p>
                {child.description && (
                  <p className="type-body-sm text-foreground">
                    {child.description}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
      {/* Right: testimonials */}
      <div className="w-[280px] flex-shrink-0">
        <p className="type-label-md mb-4 pt-2 text-neutral-600">
          {t("seeCustomers")}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onPlayVideo(blobMediaUrl("/videos/marco.mp4"))}
            className="group relative block h-[150px] w-[120px] cursor-pointer overflow-hidden rounded-[16px]"
            aria-label="Play Marco's testimonial video"
          >
            <Image
              src={blobMediaUrl("/videos/marco-poster.jpg")}
              alt=""
              width={120}
              height={150}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/25">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="currentColor"
                  className="text-aubergine-900 ml-0.5"
                >
                  <path d="M2 1l8 5-8 5V1z" />
                </svg>
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => onPlayVideo(blobMediaUrl("/videos/lucia.mp4"))}
            className="group relative block h-[150px] w-[120px] cursor-pointer overflow-hidden rounded-[16px]"
            aria-label="Play Lucia's testimonial video"
          >
            <Image
              src={blobMediaUrl("/videos/lucia-poster.jpg")}
              alt=""
              width={120}
              height={150}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/25">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="currentColor"
                  className="text-aubergine-900 ml-0.5"
                >
                  <path d="M2 1l8 5-8 5V1z" />
                </svg>
              </div>
            </div>
          </button>
        </div>
        <Link
          href={localePath("/reviews")}
          className="hover:text-aubergine-900 mt-3 inline-flex items-center gap-1 text-sm text-neutral-500 transition-colors"
        >
          {t("moreTestimonials")}
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              d="M2 10L10 2M10 2H4M10 2v6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>
      </div>
    </div>
  )
}

export function WhatWeOfferMegaMenu({
  navHiddenPaths = [],
}: {
  /** Bare `/tests/<slug>` paths hidden from the menu for this country (CMS
   *  `services.marketStatus[].hideFromNav`). */
  navHiddenPaths?: string[]
}) {
  const t = useTranslations("nav")
  const ut = useTranslations("ui")
  const localePath = useLocalePath()
  const { code: country } = useCountry()
  const prices = useLivePrices(country)
  const mriComingSoon = isComingSoon(country, "mri")
  // The market's base blood check-up: `prevention-starter` (Vorsorge-Starter)
  // on DE, `advanced-blood-test` everywhere else. Single source of truth so the
  // desktop mega-menu links + labels the same blood product as the nav hook and
  // footer, instead of hardcoding the Advanced blood test into every market.
  const baseBlood = baseBloodTest(country)
  // Hide a service link when its bare `/tests/<slug>` path is flagged
  // hide-from-nav for this country, OR when the route doesn't exist in this
  // country at all. The second half matters: `/tests/lpa`,
  // `/tests/cortisol-blood-test` and `/tests/psa-blood-test` are all `page:`
  // gated, and this menu was linking them in every market — a 404 behind a
  // nav item on AT/NL/DE. `navHiddenPaths` is CMS service state and knows
  // nothing about route gates, so both checks are needed.
  const isNavHidden = (path: string) =>
    navHiddenPaths.includes(path) ||
    !isPathAllowedInCountry(path, country as Country)
  // Imaging add-ons are hidden on DE until MRI launches there (mirrors the
  // lean DE footer + nav). A launch wave over a whole product family, which
  // is neither a page allow-list nor something CMS per-service state can say.
  // country-gate-ignore: launch-wave hold on the imaging family, not a gate
  const imagingHeldForLaunch = country !== "de"
  // Each surviving add-on can still be pulled on its own by CMS service state
  // or by its page gate, so the "Add-ons" heading follows the links.
  const showImagingAddOns =
    imagingHeldForLaunch &&
    IMAGING_ADD_ON_PATHS.some((path) => !isNavHidden(path))
  // Germany sells the blood check-up and its blood add-ons, nothing else, so
  // every German imaging service is hidden by now (CMS `hideFromNav` for the
  // six with a products row, page gates for prostate/pelvic MRI) — which left
  // the "Bildgebung" heading standing over an empty column. Keep the column
  // only while it still has something in it.
  const showImagingColumn =
    !isNavHidden("/tests/full-body-mri") || showImagingAddOns
  return (
    <div className="ml-auto max-w-[1100px] px-8 py-6">
      <div className="flex items-start gap-8">
        {/* Left side: Imaging + Blood tests columns. */}
        <div className="flex flex-1 flex-col gap-5">
          <div className="flex items-start gap-8">
            {/* Column 1: Imaging. Waitlist markets (NL) lead with the launch
                product instead — blood first, imaging (coming-soon) second —
                via flex order so the columns stay one JSX block each. */}
            {showImagingColumn && (
              <div
                className={cn("flex-1", isWaitlistOnly(country) && "order-2")}
              >
                <p className="type-label-md mb-1 px-4 pt-2 text-neutral-600">
                  {t("imaging")}
                </p>
                {!isNavHidden("/tests/full-body-mri") && (
                  <Link
                    href={localePath("/tests/full-body-mri")}
                    className="flex w-fit items-center gap-4 rounded-[16px] p-4 hover:bg-white"
                  >
                    <Image
                      src={blobMediaUrl(
                        "/images/homepage/service-mri-machine.jpeg"
                      )}
                      alt=""
                      width={116}
                      height={116}
                      className="h-[58px] w-[58px] rounded-[8px] object-cover"
                    />
                    <div>
                      <p className="type-title-md text-foreground">
                        {t("fullBodyMri")}
                      </p>
                      {/* Coming-soon markets have no MRI price — the pill takes
                        the price line instead of crowding the title row. */}
                      {mriComingSoon ? (
                        <div className="mt-1">
                          <ComingSoonPill
                            label={ut("comingSoon")}
                            variant="light"
                          />
                        </div>
                      ) : (
                        t("fromChf1990", {
                          price: prices.fullPrices.core ?? "",
                        }) && (
                          <p className="type-body-sm text-foreground">
                            {t("fromChf1990", {
                              price: prices.fullPrices.core ?? "",
                            })}
                          </p>
                        )
                      )}
                    </div>
                  </Link>
                )}
                {showImagingAddOns && (
                  <>
                    <p className="type-label-md mt-4 mb-2 px-4 text-neutral-600">
                      {t("addOns")}
                    </p>
                    <div className="flex flex-col gap-1">
                      {!isNavHidden("/tests/calcium-score") && (
                        <Link
                          href={localePath("/tests/calcium-score")}
                          className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                        >
                          {t("calciumScore")}
                        </Link>
                      )}
                      {!isNavHidden("/tests/body-composition") && (
                        <Link
                          href={localePath("/tests/body-composition")}
                          className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                        >
                          {t("bodyCompositionScore")}
                        </Link>
                      )}
                      {!isNavHidden("/tests/dexa-scan") && (
                        <Link
                          href={localePath("/tests/dexa-scan")}
                          className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                        >
                          {t("dexaScan")}
                        </Link>
                      )}
                      {!isNavHidden("/tests/brain-volumetry") && (
                        <Link
                          href={localePath("/tests/brain-volumetry")}
                          className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                        >
                          {t("brainAnalysis")}
                        </Link>
                      )}
                      {!isNavHidden("/tests/mamma-mri") && (
                        <Link
                          href={localePath("/tests/mamma-mri")}
                          className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                        >
                          {t("mammaMri")}
                        </Link>
                      )}
                      {!isNavHidden("/tests/prostate-mri") && (
                        <Link
                          href={localePath("/tests/prostate-mri")}
                          className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                        >
                          {t("prostateMri")}
                        </Link>
                      )}
                      {!isNavHidden("/tests/pelvic-mri") && (
                        <Link
                          href={localePath("/tests/pelvic-mri")}
                          className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                        >
                          {t("pelvicMri")}
                        </Link>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Column 2: Blood tests */}
            <div className={cn("flex-1", isWaitlistOnly(country) && "order-1")}>
              <p className="type-label-md mb-1 px-4 pt-2 text-neutral-600">
                {t("bloodTests")}
              </p>
              {!isNavHidden(`/tests/${baseBlood.slug}`) && (
                <Link
                  href={localePath(`/tests/${baseBlood.slug}`)}
                  className="flex w-fit items-center gap-4 rounded-[16px] p-4 hover:bg-white"
                >
                  <Image
                    src={blobMediaUrl(
                      "/images/homepage/service-blood-cells.jpeg"
                    )}
                    alt=""
                    width={116}
                    height={116}
                    className="h-[58px] w-[58px] rounded-[8px] object-cover"
                  />
                  <div>
                    <p className="type-title-md text-foreground">
                      {t(baseBlood.labelKey)}
                    </p>
                    {/* DE's base blood product (Vorsorge-Starter) has no Stripe
                        price in the feed and its detail page hides EUR prices,
                        so no CHF/blood price row is shown for it — the money
                        rule (never a currency literal, hide before you
                        mislabel). Markets that carry a blood price keep it. */}
                    {country !== "de" && (
                      <p className="type-body-sm text-foreground">
                        {t("fromChf299", {
                          price: prices.fullPrices.blood ?? "",
                        })}
                      </p>
                    )}
                  </div>
                </Link>
              )}
              <p className="type-label-md mt-4 mb-2 px-4 text-neutral-600">
                {t("addOns")}
              </p>
              <div className="flex flex-col gap-1">
                {!isNavHidden("/tests/essential-b-vitamins-test") && (
                  <Link
                    href={localePath("/tests/essential-b-vitamins-test")}
                    className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                  >
                    {t("bVitaminsBloodTest")}
                  </Link>
                )}
                {!isNavHidden("/tests/hormone-blood-test-women") && (
                  <Link
                    href={localePath("/tests/hormone-blood-test-women")}
                    className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                  >
                    {t("hormoneBloodTestWomen")}
                  </Link>
                )}
                {!isNavHidden("/tests/hormone-blood-test-men") && (
                  <Link
                    href={localePath("/tests/hormone-blood-test-men")}
                    className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                  >
                    {t("hormoneBloodTestMen")}
                  </Link>
                )}
                {!isNavHidden("/tests/omega-blood-test") && (
                  <Link
                    href={localePath("/tests/omega-blood-test")}
                    className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                  >
                    {t("omega36BloodTest")}
                  </Link>
                )}
                {!isNavHidden("/tests/lpa") && (
                  <Link
                    href={localePath("/tests/lpa")}
                    className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                  >
                    {t("lpaBloodTest")}
                  </Link>
                )}
                {!isNavHidden("/tests/cortisol-blood-test") && (
                  <Link
                    href={localePath("/tests/cortisol-blood-test")}
                    className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                  >
                    {t("cortisolBloodTest")}
                  </Link>
                )}
                {!isNavHidden("/tests/psa-blood-test") && (
                  <Link
                    href={localePath("/tests/psa-blood-test")}
                    className="type-title-md text-foreground w-fit rounded-full px-4 py-1 hover:bg-white"
                  >
                    {t("psaBloodTest")}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right: Ahead membership promo card */}
        <MembershipMegaMenuCard />
      </div>
    </div>
  )
}

function MembershipMegaMenuCard() {
  const t = useTranslations("nav")
  // Reuses the existing `locations.comingSoon` string ("Bald verfügbar").
  const lt = useTranslations("locations")
  const localePath = useLocalePath()
  const { code: country } = useCountry()
  // Membership is sold per-market: show a "coming soon" pill on the card
  // wherever membership booking isn't open yet but is on the way (DE checkup).
  // CH (membership live) shows no pill.
  const membershipComingSoon = !isBookingOpen(country, "membership")
  // German runs longer than the EN/FR/NL copy, so step the headline down
  // a token (28→24) to keep "Tests im Verlauf." / "Immer für Sie da."
  // on three lines without wrapping.
  const locale = useLocale()
  const headlineSize = locale === "de" ? "type-headline-sm" : "type-headline-md"
  // The nav only advertises membership where the market sells it
  // (section:nav.membership). Waitlist NL never promised it — no card, no
  // promise (AHD-9361) — and DE dropped the promise together with the rest of
  // its non-blood nav, then the page itself (AHD-13290).
  if (!isAllowedInCountry("section:nav.membership", country)) {
    return null
  }
  return (
    <Link
      href={localePath("/membership")}
      // Fixed `h-[374px]` per Figma 342:6754 — the gradient stops are tuned
      // to this height so the cream wash clears the subject's face.
      className="group relative flex h-[374px] w-[314px] flex-shrink-0 flex-col justify-between overflow-hidden rounded-[16px] bg-white pt-5 pb-4"
    >
      {/* Coming-soon pill — membership isn't bookable yet in this market
          (DE/NL pre-launch + `checkup`). Same pill as the Ganzkörper-MRT item
          (light variant, olive dot), top-right of the card. */}
      {membershipComingSoon && (
        <ComingSoonPill
          label={lt("comingSoon")}
          variant="light"
          className="absolute top-4 right-4 z-10"
        />
      )}
      {/* Sunset portrait per Figma 342:6754 — subject on the right edge
          looking into the content, atmosphere on the left for the heading. */}
      <Image
        src={blobMediaUrl("/images/homepage-membership/menu-card.png")}
        alt=""
        aria-hidden="true"
        fill
        sizes="314px"
        className="object-cover"
      />
      {/* Cream wash for heading + price readability over the photo. Opacity
          fades MONOTONICALLY from the top and is fully transparent by ~55% —
          above the subject's face — so the lower half (face + CTA pill) shows
          the lifestyle photo cleanly. (The old gradient peaked at an opaque
          white mid-stop, which washed over the face.) */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-[rgba(228,243,248,0.92)] via-[rgba(228,243,248,0.8)] via-38% to-transparent to-55%" // ds-color-ignore: gradient stops
      />
      <div
        className={cn(
          "relative flex flex-col",
          // When the coming-soon pill is shown, drop the heading so it clears
          // the pill — gives the top-right pill its own breathing room.
          membershipComingSoon && "pt-6"
        )}
      >
        <div className="px-6 py-2 pr-8">
          <p className={`${headlineSize} text-foreground`}>
            {t("megaMenuMembershipHeading1")}
            <br />
            {t("megaMenuMembershipHeading2")}
            <br />
            {t("megaMenuMembershipHeading3")}
          </p>
        </div>
        {t("megaMenuMembershipPrice") && (
          <div className="px-6 py-2 pr-8">
            <p className="type-title-md text-foreground">
              {t("megaMenuMembershipPrice")}
            </p>
          </div>
        )}
      </div>
      <div className="relative px-6 py-2 pr-8">
        <span className="inline-flex h-11 items-center gap-2 rounded-full bg-white/80 px-8 py-[9px] backdrop-blur-sm transition-colors group-hover:bg-white">
          <span className="type-title-md text-foreground">
            {t("megaMenuMembershipCta")}
          </span>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="transition-transform duration-200 group-hover:translate-x-0.5"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </span>
      </div>
    </Link>
  )
}
